// src/features/bom/pages/ProductionPage.tsx
// ENHANCED — Chef Command Center, Active Batch Top-Up, Waste Tracking & Modern Food UI

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { Button } from '@/components/ui/Button';
import {
    getAllRecipes, BomRecipe, startProduction, getTodayBatches,
    completeBatch, addPortions
} from '../services/bomApi';
import { getProductionFloors, ProductionFloor } from '../services/productionFloorApi';
import {
    ArrowLeft, Plus, ChefHat, Clock, Package, CheckCircle2,
    FlaskConical, TrendingUp, DollarSign, AlertCircle, Timer,
    Factory, Layers, Flame, Target, PieChart, Zap,
    RefreshCw, ChevronRight, BarChart3, Utensils, Sparkles,
    Trash2, AlertTriangle, Check
} from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { clsx } from 'clsx';
import confetti from 'canvas-confetti';
import apiClient from '@/lib/api-client';

// Food emoji mapper
function getFoodEmoji(name: string): string {
    const text = (name || '').toLowerCase();
    if (text.includes('jollof') || text.includes('fried rice') || text.includes('ofada') || text.includes('rice')) return '🍚';
    if (text.includes('chicken') || text.includes('poultry') || text.includes('wings') || text.includes('drumstick')) return '🍗';
    if (text.includes('beef') || text.includes('steak') || text.includes('suya') || text.includes('meat') || text.includes('goat') || text.includes('asun')) return '🥩';
    if (text.includes('fish') || text.includes('tilapia') || text.includes('salmon') || text.includes('catfish') || text.includes('seafood')) return '🐟';
    if (text.includes('soup') || text.includes('stew') || text.includes('egusi') || text.includes('ogbono') || text.includes('pepper soup')) return '🍲';
    if (text.includes('burger') || text.includes('sandwich')) return '🍔';
    if (text.includes('pizza')) return '🍕';
    if (text.includes('shawarma') || text.includes('wrap') || text.includes('roll')) return '🌯';
    if (text.includes('salad') || text.includes('veggie')) return '🥗';
    if (text.includes('pasta') || text.includes('spaghetti') || text.includes('noodles')) return '🍝';
    if (text.includes('bread') || text.includes('toast') || text.includes('pastry') || text.includes('croissant')) return '🥐';
    if (text.includes('cake') || text.includes('donut') || text.includes('dessert')) return '🍰';
    if (text.includes('plantain') || text.includes('dodo') || text.includes('fries') || text.includes('chips')) return '🍟';
    if (text.includes('yam') || text.includes('potato')) return '🥔';
    if (text.includes('drink') || text.includes('juice') || text.includes('smoothie')) return '🧃';
    return '🍽️';
}

export function ProductionPage() {
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    const [showStartModal, setShowStartModal] = useState(false);
    const [selectedRecipe, setSelectedRecipe] = useState<BomRecipe | null>(null);
    const [quantityToProduce, setQuantityToProduce] = useState('');
    const [productionNotes, setProductionNotes] = useState('');
    const [selectedFloorId, setSelectedFloorId] = useState('');

    // Modal states for Top-up & End batch
    const [topUpBatch, setTopUpBatch] = useState<any | null>(null);
    const [topUpAmount, setTopUpAmount] = useState('10');
    const [endBatch, setEndBatch] = useState<any | null>(null);
    const [wastedPortions, setWastedPortions] = useState('0');
    const [wasteReason, setWasteReason] = useState('End of Service');

    const { data: recipes } = useQuery({
        queryKey: ['bom-recipes', user?.tenantId],
        queryFn: () => getAllRecipes(user!.tenantId),
        enabled: !!user?.tenantId,
    });

    const { data: floors } = useQuery({
        queryKey: ['production-floors-active', user?.tenantId],
        queryFn: () => getProductionFloors(user!.tenantId, { status: 'active' }),
        enabled: !!user?.tenantId,
    });

    const { data: todayBatches, isLoading, refetch } = useQuery({
        queryKey: ['production-today', user?.tenantId],
        queryFn: () => getTodayBatches(user!.tenantId),
        enabled: !!user?.tenantId,
        refetchInterval: 15000,
    });

    useEffect(() => {
        if (floors && floors.length > 0 && !selectedFloorId) {
            setSelectedFloorId(floors[0].id);
        }
    }, [floors]);

    // Start production mutation
    const startProductionMutation = useMutation({
        mutationFn: (data: { recipeId: string; quantityProduced: number; notes?: string; branchId?: string }) =>
            startProduction(user!.tenantId, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['production-today'] });
            queryClient.invalidateQueries({ queryKey: ['production-today-pos'] });
            confetti({
                particleCount: 80,
                spread: 60,
                origin: { y: 0.6 }
            });
            addToast({ type: 'success', title: 'Batch Cooking Started! 🍳', message: 'Inventory deducted and portions added to Restaurant POS.' });
            setShowStartModal(false);
            setSelectedRecipe(null);
            setQuantityToProduce('');
            setProductionNotes('');
        },
        onError: (err: any) => {
            addToast({ type: 'error', title: 'Production Failed', message: err?.response?.data?.error?.message || err?.message });
        },
    });

    // Add portions to existing active batch
    const addPortionsMutation = useMutation({
        mutationFn: (data: { batchId: string; quantityToAdd: number }) =>
            addPortions(
                user!.tenantId,
                data.batchId,
                data.quantityToAdd,
                topUpBatch?.branchId || topUpBatch?.productionFloorId || (selectedFloorId ? selectedFloorId : undefined)
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['production-today'] });
            queryClient.invalidateQueries({ queryKey: ['production-today-pos'] });
            addToast({ type: 'success', title: 'Portions Added! 🍲', message: 'Kitchen batch top-up recorded.' });
            setTopUpBatch(null);
            setTopUpAmount('10');
        },
        onError: (err: any) => {
            addToast({ type: 'error', title: 'Failed to add portions', message: err?.response?.data?.error?.message || err?.message });
        },
    });

    // Complete / Close batch with optional waste tracking
    const completeBatchMutation = useMutation({
        mutationFn: (data: { batchId: string; wastedPortions: number }) =>
            completeBatch(user!.tenantId, data.batchId, data.wastedPortions),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['production-today'] });
            queryClient.invalidateQueries({ queryKey: ['production-today-pos'] });
            addToast({ type: 'success', title: 'Batch Closed ✅', message: 'Remaining portions closed out.' });
            setEndBatch(null);
            setWastedPortions('0');
        },
        onError: (err: any) => {
            addToast({ type: 'error', title: 'Failed to close batch', message: err?.message });
        }
    });

    const activeBatches = (todayBatches || []).filter((b: any) => b.status === 'active');
    const depletedBatches = (todayBatches || []).filter((b: any) => b.status === 'depleted' || b.status === 'completed');
    const activeRecipes = (recipes || []).filter(r => r.isActive);
    const selectedFloor = (floors || []).find(f => f.id === selectedFloorId);

    const formatNaira = (val: number) => `₦${val.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    const handleStartProduction = () => {
        if (!selectedRecipe || !quantityToProduce) return;
        startProductionMutation.mutate({
            recipeId: selectedRecipe.id,
            quantityProduced: parseFloat(quantityToProduce),
            notes: productionNotes || undefined,
            branchId: selectedFloorId,
        });
    };

    // Live calculations for Start Production modal preview
    const startModalEstimates = useMemo(() => {
        if (!selectedRecipe || !quantityToProduce) return null;
        const qty = parseFloat(quantityToProduce) || 0;
        const costPerYield = selectedRecipe.costPerYield || (selectedRecipe.totalCost ? selectedRecipe.totalCost / (parseFloat(selectedRecipe.yieldQuantity) || 1) : 0);
        const estCost = costPerYield * qty;
        const sellingPrice = parseFloat(selectedRecipe.finishedProductRetailPrice || '0') || (costPerYield * 1.5);
        const estRevenue = sellingPrice * qty;
        const estProfit = estRevenue - estCost;
        return { estCost, estRevenue, estProfit };
    }, [selectedRecipe, quantityToProduce]);

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50/20 font-sans">
            {/* Header */}
            <header className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-xs">
                <div className="max-w-[1600px] mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button onClick={() => navigate('/bom')} className="p-2 rounded-xl text-slate-500 hover:bg-slate-100 transition-colors" type="button">
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        <div className="p-2.5 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl shadow-md shadow-amber-500/20 text-white">
                            <ChefHat className="w-6 h-6" />
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h1 className="text-xl font-extrabold text-slate-900 tracking-tight">Kitchen Production</h1>
                                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800">
                                    Chef Station
                                </span>
                            </div>
                            <p className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                                <strong>{activeBatches.length}</strong> active batches cooking · <strong>{depletedBatches.length}</strong> completed today
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate('/bom-pos')}
                            className="hidden md:flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors"
                        >
                            <Utensils size={15} /> Switch to Restaurant POS
                        </button>

                        {(floors || []).length > 0 && (
                            <select
                                value={selectedFloorId}
                                onChange={(e) => setSelectedFloorId(e.target.value)}
                                className="px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 focus:ring-2 focus:ring-amber-500/20 outline-none"
                            >
                                {(floors || []).map(f => (
                                    <option key={f.id} value={f.id}>{f.name}</option>
                                ))}
                            </select>
                        )}

                        <Button onClick={() => refetch()} variant="outline" size="sm" leftIcon={<RefreshCw size={14} />}>
                            Refresh
                        </Button>

                        <button
                            onClick={() => setShowStartModal(true)}
                            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-xl font-bold text-xs shadow-md shadow-amber-500/20 hover:scale-[1.02] transition-all"
                        >
                            <Plus size={16} /> Cook New Batch
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-6">
                {/* Kitchen Metrics */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3.5">
                    <QuickStat label="Active Batches" value={activeBatches.length} icon={Flame} gradient="from-amber-500 to-orange-600" />
                    <QuickStat label="Completed Today" value={depletedBatches.length} icon={CheckCircle2} gradient="from-emerald-500 to-teal-600" />
                    <QuickStat label="Available Recipes" value={activeRecipes.length} icon={FlaskConical} gradient="from-purple-500 to-indigo-600" />
                    <QuickStat label="Portions Cooked" value={activeBatches.reduce((s: number, b: any) => s + parseFloat(b.quantityProduced || '0'), 0)} icon={Factory} gradient="from-blue-500 to-indigo-600" isDecimal />
                    <QuickStat label="Portions Remaining" value={activeBatches.reduce((s: number, b: any) => s + parseFloat(b.portionsRemaining || '0'), 0)} icon={Target} gradient="from-rose-500 to-red-600" isDecimal />
                </div>

                {isLoading ? (
                    <div className="flex flex-col items-center justify-center py-24 text-slate-400">
                        <Spinner size="xl" />
                        <p className="mt-3 text-sm font-medium">Loading kitchen station...</p>
                    </div>
                ) : (
                    <>
                        {/* Active Batches Section */}
                        {activeBatches.length > 0 && (
                            <section className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <h2 className="text-base font-extrabold text-slate-800 flex items-center gap-2 tracking-tight">
                                        <Flame size={18} className="text-amber-500" /> Active Kitchen Batches ({activeBatches.length})
                                    </h2>
                                    <span className="text-xs text-slate-400">Ready to sell in Restaurant POS</span>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                                    {activeBatches.map((batch: any) => {
                                        const produced = parseFloat(batch.quantityProduced) || 0;
                                        const remaining = parseFloat(batch.portionsRemaining) || 0;
                                        const sold = Math.max(0, produced - remaining);
                                        const progress = produced > 0 ? (sold / produced) * 100 : 0;
                                        const remainingPct = produced > 0 ? (remaining / produced) * 100 : 0;
                                        const emoji = getFoodEmoji(batch.recipeName);

                                        // Color coded gauge
                                        const gaugeGradient = remainingPct >= 50
                                            ? 'from-emerald-400 to-teal-500'
                                            : remainingPct >= 20
                                            ? 'from-amber-400 to-orange-500'
                                            : 'from-rose-400 to-red-500';

                                        const sellingPriceNum = parseFloat(batch.sellingPrice) || 0;
                                        const estRevenueGenerated = sellingPriceNum * sold;

                                        return (
                                            <motion.div
                                                key={batch.id}
                                                initial={{ opacity: 0, y: 15 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                whileHover={{ y: -3 }}
                                                className="bg-white rounded-3xl border-2 border-amber-200/80 p-5 shadow-sm hover:shadow-xl hover:shadow-amber-500/10 transition-all flex flex-col justify-between"
                                            >
                                                <div>
                                                    {/* Header: Emoji & Batch Tag */}
                                                    <div className="flex items-start justify-between gap-3 mb-3">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-12 h-12 rounded-2xl bg-amber-100 flex items-center justify-center text-2xl shadow-xs">
                                                                {emoji}
                                                            </div>
                                                            <div>
                                                                <h3 className="font-bold text-slate-900 line-clamp-1">{batch.recipeName}</h3>
                                                                <p className="text-[11px] text-slate-400 font-mono">#{batch.batchNumber}</p>
                                                            </div>
                                                        </div>
                                                        <span className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-extrabold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Live
                                                        </span>
                                                    </div>

                                                    {/* Visual Portion Meter */}
                                                    <div className="bg-slate-50 p-3.5 rounded-2xl border border-slate-100 mb-4">
                                                        <div className="flex justify-between text-xs mb-1.5">
                                                            <span className="font-bold text-slate-800">{remaining.toLocaleString()} portions left</span>
                                                            <span className="text-slate-500 font-medium">{sold.toLocaleString()} sold</span>
                                                        </div>
                                                        <div className="w-full bg-slate-200 rounded-full h-2.5 overflow-hidden">
                                                            <motion.div
                                                                initial={{ width: 0 }}
                                                                animate={{ width: `${Math.max(remainingPct, 5)}%` }}
                                                                className={clsx('h-2.5 rounded-full bg-gradient-to-r transition-all duration-500', gaugeGradient)}
                                                            />
                                                        </div>
                                                        <div className="flex justify-between text-[10px] text-slate-400 mt-1">
                                                            <span>Total batch: {produced.toLocaleString()}</span>
                                                            <span>{progress.toFixed(0)}% sold</span>
                                                        </div>
                                                    </div>

                                                    {/* Financial Breakdown */}
                                                    <div className="grid grid-cols-2 gap-2.5 text-xs mb-4">
                                                        <div className="p-2.5 bg-slate-50 rounded-xl">
                                                            <p className="text-[10px] text-slate-400 font-semibold uppercase">Cost / Portion</p>
                                                            <p className="font-bold text-purple-700 mt-0.5">{formatNaira(parseFloat(batch.costPerPortion))}</p>
                                                        </div>
                                                        <div className="p-2.5 bg-slate-50 rounded-xl">
                                                            <p className="text-[10px] text-slate-400 font-semibold uppercase">Selling Price</p>
                                                            <p className="font-bold text-emerald-700 mt-0.5">{formatNaira(sellingPriceNum)}</p>
                                                        </div>
                                                        <div className="p-2.5 bg-slate-50 rounded-xl">
                                                            <p className="text-[10px] text-slate-400 font-semibold uppercase">Total Batch Cost</p>
                                                            <p className="font-bold text-slate-700 mt-0.5">{formatNaira(parseFloat(batch.totalCost))}</p>
                                                        </div>
                                                        <div className="p-2.5 bg-slate-50 rounded-xl">
                                                            <p className="text-[10px] text-slate-400 font-semibold uppercase">Sales Generated</p>
                                                            <p className="font-bold text-emerald-600 mt-0.5">{formatNaira(estRevenueGenerated)}</p>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Action Buttons: Add portions & End Batch */}
                                                <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-100">
                                                    <button
                                                        onClick={() => {
                                                            setTopUpBatch(batch);
                                                            setTopUpAmount('10');
                                                        }}
                                                        className="flex items-center justify-center gap-1.5 py-2.5 bg-amber-50 text-amber-800 border border-amber-200 rounded-xl text-xs font-bold hover:bg-amber-100 transition-colors"
                                                    >
                                                        <Plus size={14} /> Add Portions
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setEndBatch(batch);
                                                            setWastedPortions('0');
                                                        }}
                                                        className="py-2.5 border border-slate-200 text-slate-600 rounded-xl text-xs font-semibold hover:bg-rose-50 hover:text-rose-700 hover:border-rose-200 transition-colors"
                                                    >
                                                        End Batch
                                                    </button>
                                                </div>
                                            </motion.div>
                                        );
                                    })}
                                </div>
                            </section>
                        )}

                        {/* Completed Batches Archive */}
                        {depletedBatches.length > 0 && (
                            <section className="space-y-3 pt-4">
                                <div className="flex items-center justify-between">
                                    <h2 className="text-base font-extrabold text-slate-800 flex items-center gap-2 tracking-tight">
                                        <CheckCircle2 size={18} className="text-emerald-500" /> Completed Today ({depletedBatches.length})
                                    </h2>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                    {depletedBatches.map((batch: any) => (
                                        <div key={batch.id} className="bg-white rounded-2xl border border-slate-200 p-4 opacity-80 hover:opacity-100 transition-opacity">
                                            <div className="flex items-start justify-between mb-2">
                                                <div>
                                                    <h3 className="font-bold text-slate-900 text-sm line-clamp-1">{batch.recipeName}</h3>
                                                    <p className="text-[10px] text-slate-400 font-mono">#{batch.batchNumber}</p>
                                                </div>
                                                <Badge variant={batch.status === 'depleted' ? 'danger' : 'success'} size="sm">
                                                    {batch.status === 'depleted' ? 'Sold Out' : 'Closed'}
                                                </Badge>
                                            </div>
                                            <div className="grid grid-cols-2 gap-2 text-xs text-slate-500 mt-2">
                                                <div>
                                                    <p className="text-[10px] text-slate-400">Cooked</p>
                                                    <p className="font-bold text-slate-700">{parseFloat(batch.quantityProduced).toLocaleString()}</p>
                                                </div>
                                                <div>
                                                    <p className="text-[10px] text-slate-400">Total Cost</p>
                                                    <p className="font-bold text-slate-700">{formatNaira(parseFloat(batch.totalCost))}</p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* No Batches Empty State */}
                        {todayBatches?.length === 0 && (
                            <div className="text-center py-20 bg-white rounded-3xl border border-slate-200 shadow-xs max-w-xl mx-auto p-8">
                                <div className="w-20 h-20 rounded-3xl bg-amber-50 flex items-center justify-center mx-auto mb-4 text-amber-500 shadow-inner">
                                    <ChefHat size={44} />
                                </div>
                                <h2 className="text-xl font-extrabold text-slate-800">No Kitchen Production Today</h2>
                                <p className="text-xs text-slate-500 mt-2 max-w-sm mx-auto">
                                    Cook your first batch to deduct raw materials and stock up the Restaurant POS register.
                                </p>
                                <button
                                    onClick={() => setShowStartModal(true)}
                                    className="mt-5 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-xl text-xs font-bold shadow-md shadow-amber-500/20 hover:scale-[1.02] transition-all"
                                >
                                    + Start First Batch
                                </button>
                            </div>
                        )}
                    </>
                )}
            </main>

            {/* Start Production Modal */}
            <AnimatePresence>
                {showStartModal && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                        onClick={() => setShowStartModal(false)}>
                        <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
                            className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden" onClick={e => e.stopPropagation()}>
                            <div className="bg-gradient-to-r from-amber-500 to-orange-600 px-6 py-5 text-white">
                                <h2 className="text-lg font-bold flex items-center gap-2">
                                    <ChefHat size={20} /> Cook New Batch
                                </h2>
                                <p className="text-xs text-white/80 mt-0.5">Deducts raw materials from inventory and enables sales on POS</p>
                            </div>
                            <div className="p-6 space-y-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-600 uppercase mb-1.5">Select Recipe</label>
                                    <select
                                        value={selectedRecipe?.id || ''}
                                        onChange={(e) => {
                                            const recipe = activeRecipes.find(r => r.id === e.target.value);
                                            setSelectedRecipe(recipe || null);
                                            if (recipe) setQuantityToProduce(recipe.yieldQuantity);
                                        }}
                                        className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400 outline-none"
                                    >
                                        <option value="">Choose a recipe to cook...</option>
                                        {activeRecipes.map(r => (
                                            <option key={r.id} value={r.id}>
                                                {getFoodEmoji(r.name)} {r.name} (Yield: {r.yieldQuantity} {r.yieldUomName || 'portions'})
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {selectedRecipe && (
                                    <>
                                        <div className="p-4 bg-amber-50/70 border border-amber-200/60 rounded-2xl text-xs space-y-2">
                                            <div className="grid grid-cols-2 gap-2">
                                                <p className="text-slate-600">Standard Yield: <strong className="text-slate-900">{selectedRecipe.yieldQuantity} {selectedRecipe.yieldUomName || 'portions'}</strong></p>
                                                <p className="text-slate-600">Ingredients: <strong className="text-slate-900">{selectedRecipe.items.length} items</strong></p>
                                                {selectedRecipe.preparationTime && (
                                                    <p className="text-slate-600 col-span-2">Cook Time: <strong className="text-slate-900">{selectedRecipe.preparationTime} mins</strong></p>
                                                )}
                                            </div>
                                        </div>

                                        <div>
                                            <div className="flex items-center justify-between mb-1.5">
                                                <label className="text-xs font-bold text-slate-600 uppercase">Portions to Produce</label>
                                                <div className="flex items-center gap-1">
                                                    {[1, 2, 3, 5, 10].map(mult => {
                                                        const baseQty = parseFloat(selectedRecipe.yieldQuantity) || 1;
                                                        const targetQty = (baseQty * mult).toString();
                                                        return (
                                                            <button
                                                                key={mult}
                                                                type="button"
                                                                onClick={() => setQuantityToProduce(targetQty)}
                                                                className={clsx(
                                                                    'px-2 py-0.5 rounded-md text-[10px] font-bold border transition-colors',
                                                                    quantityToProduce === targetQty
                                                                        ? 'bg-amber-500 text-white border-amber-500'
                                                                        : 'bg-slate-100 text-slate-600 border-slate-200 hover:bg-amber-50'
                                                                )}
                                                            >
                                                                {mult}x
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                            <input
                                                type="number"
                                                min="1"
                                                value={quantityToProduce}
                                                onChange={(e) => setQuantityToProduce(e.target.value)}
                                                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/20 focus:border-amber-400 outline-none font-bold"
                                            />
                                        </div>

                                        {/* Financial projection preview */}
                                        {startModalEstimates && (
                                            <div className="grid grid-cols-3 gap-2 p-3 bg-slate-50 border border-slate-200 rounded-2xl text-center">
                                                <div>
                                                    <p className="text-[10px] text-slate-400 font-semibold uppercase">Raw Cost</p>
                                                    <p className="font-bold text-purple-700 text-xs mt-0.5">{formatNaira(startModalEstimates.estCost)}</p>
                                                </div>
                                                <div>
                                                    <p className="text-[10px] text-slate-400 font-semibold uppercase">Est. Revenue</p>
                                                    <p className="font-bold text-emerald-700 text-xs mt-0.5">{formatNaira(startModalEstimates.estRevenue)}</p>
                                                </div>
                                                <div>
                                                    <p className="text-[10px] text-slate-400 font-semibold uppercase">Est. Profit</p>
                                                    <p className="font-bold text-emerald-600 text-xs mt-0.5">{formatNaira(startModalEstimates.estProfit)}</p>
                                                </div>
                                            </div>
                                        )}

                                        <div>
                                            <label className="block text-xs font-bold text-slate-600 uppercase mb-1.5">Batch Notes (Optional)</label>
                                            <textarea
                                                value={productionNotes}
                                                onChange={(e) => setProductionNotes(e.target.value)}
                                                rows={2}
                                                placeholder="e.g., Morning rush prep, extra seasoning added..."
                                                className="w-full px-4 py-2.5 border-2 border-slate-200 rounded-xl text-xs resize-none focus:ring-2 focus:ring-amber-500/20 outline-none"
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
                                <button
                                    onClick={() => setShowStartModal(false)}
                                    className="px-5 py-2.5 border border-slate-200 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-100"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleStartProduction}
                                    disabled={!selectedRecipe || !quantityToProduce || startProductionMutation.isPending}
                                    className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-xl font-bold text-xs shadow-md shadow-amber-500/20 disabled:opacity-50 hover:opacity-95"
                                >
                                    {startProductionMutation.isPending ? <Spinner size="xs" variant="white" /> : <ChefHat size={16} />}
                                    Start Production
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Top Up Batch Portions Modal */}
            <AnimatePresence>
                {topUpBatch && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                        onClick={() => setTopUpBatch(null)}>
                        <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
                            className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()}>
                            <div className="bg-gradient-to-r from-amber-500 to-orange-600 px-6 py-5 text-white">
                                <h3 className="text-base font-bold flex items-center gap-2">
                                    <Plus size={18} /> Top-Up Batch Portions
                                </h3>
                                <p className="text-xs text-white/80 mt-0.5">{topUpBatch.recipeName} (#{topUpBatch.batchNumber})</p>
                            </div>
                            <div className="p-6 space-y-4">
                                <div className="p-3 bg-amber-50 rounded-2xl text-xs text-slate-600">
                                    Currently remaining: <strong className="text-slate-900">{parseFloat(topUpBatch.portionsRemaining)} portions</strong>
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-slate-600 uppercase mb-1.5">Additional Portions to Add</label>
                                    <div className="flex items-center gap-1.5 mb-2">
                                        {[5, 10, 20, 50].map(amt => (
                                            <button
                                                key={amt}
                                                type="button"
                                                onClick={() => setTopUpAmount(amt.toString())}
                                                className={clsx(
                                                    'px-3 py-1 rounded-lg text-xs font-bold border transition-colors',
                                                    topUpAmount === amt.toString()
                                                        ? 'bg-amber-500 text-white border-amber-500'
                                                        : 'bg-slate-100 text-slate-600 border-slate-200'
                                                )}
                                            >
                                                +{amt}
                                            </button>
                                        ))}
                                    </div>
                                    <input
                                        type="number"
                                        min="1"
                                        value={topUpAmount}
                                        onChange={(e) => setTopUpAmount(e.target.value)}
                                        className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl text-sm font-bold focus:ring-2 focus:ring-amber-500/20 outline-none"
                                    />
                                    <p className="text-[11px] text-slate-400 mt-1.5">
                                        Raw ingredients will be deducted proportionally from inventory.
                                    </p>
                                </div>
                            </div>
                            <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
                                <button
                                    onClick={() => setTopUpBatch(null)}
                                    className="px-4 py-2 border border-slate-200 rounded-xl text-xs font-bold text-slate-600"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => addPortionsMutation.mutate({
                                        batchId: topUpBatch.id,
                                        quantityToAdd: parseFloat(topUpAmount) || 0
                                    })}
                                    disabled={!topUpAmount || parseFloat(topUpAmount) <= 0 || addPortionsMutation.isPending}
                                    className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-xl font-bold text-xs shadow-md disabled:opacity-50"
                                >
                                    {addPortionsMutation.isPending ? <Spinner size="xs" variant="white" /> : <Plus size={14} />}
                                    Confirm Top-Up
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* End Production / Close Batch Modal */}
            <AnimatePresence>
                {endBatch && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                        onClick={() => setEndBatch(null)}>
                        <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
                            className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden" onClick={e => e.stopPropagation()}>
                            <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-6 py-5 text-white">
                                <h3 className="text-base font-bold flex items-center gap-2">
                                    <CheckCircle2 size={18} className="text-emerald-400" /> End Kitchen Batch
                                </h3>
                                <p className="text-xs text-white/70 mt-0.5">{endBatch.recipeName} (#{endBatch.batchNumber})</p>
                            </div>
                            <div className="p-6 space-y-4">
                                <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-xs space-y-1">
                                    <div className="flex justify-between">
                                        <span className="text-slate-500">Total Produced:</span>
                                        <strong className="text-slate-800">{parseFloat(endBatch.quantityProduced)} portions</strong>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-slate-500">Remaining in Kitchen:</span>
                                        <strong className="text-amber-600 font-bold">{parseFloat(endBatch.portionsRemaining)} portions</strong>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-slate-600 uppercase mb-1.5">Record Wasted / Spoilage Portions</label>
                                    <div className="flex items-center gap-2 mb-2">
                                        <button
                                            type="button"
                                            onClick={() => setWastedPortions('0')}
                                            className={clsx('px-3 py-1 rounded-lg text-xs font-bold border', wastedPortions === '0' ? 'bg-emerald-500 text-white border-emerald-500' : 'bg-slate-100 text-slate-600')}
                                        >
                                            0 (Zero Waste)
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setWastedPortions(endBatch.portionsRemaining.toString())}
                                            className={clsx('px-3 py-1 rounded-lg text-xs font-bold border', wastedPortions === endBatch.portionsRemaining.toString() ? 'bg-rose-500 text-white border-rose-500' : 'bg-slate-100 text-slate-600')}
                                        >
                                            All Remaining ({parseFloat(endBatch.portionsRemaining)})
                                        </button>
                                    </div>
                                    <input
                                        type="number"
                                        min="0"
                                        max={parseFloat(endBatch.portionsRemaining)}
                                        value={wastedPortions}
                                        onChange={(e) => setWastedPortions(e.target.value)}
                                        className="w-full px-4 py-2.5 border-2 border-slate-200 rounded-xl text-sm font-bold focus:ring-2 focus:ring-amber-500/20 outline-none"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-slate-600 uppercase mb-1.5">Closure Reason</label>
                                    <select
                                        value={wasteReason}
                                        onChange={(e) => setWasteReason(e.target.value)}
                                        className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs bg-white focus:ring-2 focus:ring-amber-500/20"
                                    >
                                        <option value="End of Service">End of Service / Day</option>
                                        <option value="Expired / Overheld">Expired / Overheld Food</option>
                                        <option value="Kitchen Error / Burned">Kitchen Error / Spoilage</option>
                                        <option value="Staff Meal">Staff Meal Consumption</option>
                                    </select>
                                </div>
                            </div>
                            <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
                                <button
                                    onClick={() => setEndBatch(null)}
                                    className="px-4 py-2 border border-slate-200 rounded-xl text-xs font-bold text-slate-600"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => completeBatchMutation.mutate({
                                        batchId: endBatch.id,
                                        wastedPortions: parseFloat(wastedPortions) || 0
                                    })}
                                    disabled={completeBatchMutation.isPending}
                                    className="flex items-center gap-2 px-5 py-2 bg-slate-900 text-white rounded-xl font-bold text-xs hover:bg-black"
                                >
                                    {completeBatchMutation.isPending ? <Spinner size="xs" variant="white" /> : <Check size={14} />}
                                    Close Out Batch
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

function QuickStat({ label, value, icon: Icon, gradient, isDecimal }: {
    label: string; value: number; icon: React.ElementType; gradient: string; isDecimal?: boolean;
}) {
    const display = isDecimal ? value.toLocaleString(undefined, { maximumFractionDigits: 0 }) : value.toLocaleString();
    return (
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} whileHover={{ y: -2 }}
            className="bg-white rounded-3xl border border-slate-200/80 shadow-xs p-4 hover:shadow-md transition-all">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{label}</p>
                    <p className="text-xl font-extrabold text-slate-900 mt-1 tracking-tight">{display}</p>
                </div>
                <div className={`p-2.5 rounded-2xl bg-gradient-to-br ${gradient} shadow-md text-white`}>
                    <Icon className="w-5 h-5" />
                </div>
            </div>
        </motion.div>
    );
}